import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();

    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q7g3knlsngrm4llsq0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '草莓色研社',
    url: 'https://strawberrycolor.cn',
    description: aboutInfo.brandIntro,
    email: aboutInfo.contact.email,
    telephone: aboutInfo.contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '创意路88号草莓创意园A座302室',
      addressLocality: '杭州市',
      addressRegion: '浙江省',
      addressCountry: 'CN',
    },
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 草莓色研社',
    description: aboutInfo.brandIntro,
    url: 'https://strawberrycolor.cn/about',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="px-4 md:px-6 py-8">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mt-4 mb-6">
                关于草莓色研社
              </h1>
            </div>
          </div>

          {/* Brand Introduction */}
          <section className="px-4 md:px-6 py-10 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    品牌介绍
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-6">
                    {aboutInfo.brandIntro}
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 shrink-0 mt-0.5">
                        <i className="ri-focus-2-line text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">我们的使命</h4>
                        <p className="text-sm text-foreground-500">{aboutInfo.mission}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 shrink-0 mt-0.5">
                        <i className="ri-eye-line text-accent-600"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">我们的愿景</h4>
                        <p className="text-sm text-foreground-500">{aboutInfo.vision}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Creative%20modern%20workspace%20studio%20with%20professional%20video%20editing%20equipment%20and%20fresh%20strawberries%20on%20a%20wooden%20desk%2C%20warm%20natural%20lighting%20from%20large%20windows%2C%20cozy%20yet%20professional%20atmosphere%2C%20color%20grading%20monitors%2C%20minimalist%20interior%20design&width=700&height=500&seq=about-brand-image&orientation=landscape"
                    alt="草莓色研社工作室"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Reputation Section */}
          <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">行业口碑</span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                  值得信赖的草莓视频调色平台
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {aboutInfo.reputation.map((item, index) => (
                  <div
                    key={index}
                    className="bg-background-50 border border-background-200/70 rounded-lg p-6 hover:border-accent-200/60 transition-all duration-300"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                      <i className={`text-lg ${index === 0 ? 'ri-article-line' : index === 1 ? 'ri-group-line' : index === 2 ? 'ri-hand-heart-line' : index === 3 ? 'ri-building-4-line' : 'ri-trophy-line'}`}></i>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Team Section */}
          <section id="team" className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <span className="text-sm font-semibold text-primary-600 uppercase tracking-wider">核心团队</span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                  我们是一群热爱草莓与影像的人
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {aboutInfo.team.map((member) => (
                  <div
                    key={member.name}
                    className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center hover:border-primary-200/60 transition-all duration-300"
                  >
                    <img
                      src={member.avatarUrl}
                      alt={member.name}
                      className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
                    />
                    <h3 className="text-base font-semibold text-foreground-950 mb-1">{member.name}</h3>
                    <p className="text-xs text-primary-600 font-medium mb-3">{member.role}</p>
                    <p className="text-sm text-foreground-500 leading-relaxed">{member.bio}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones Section */}
          <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">发展历程</span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                  一路走来的重要时刻
                </h2>
              </div>
              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-px bg-background-200/70"></div>
                <div className="space-y-8">
                  {aboutInfo.milestones.map((m, index) => (
                    <div key={index} className="relative pl-14">
                      <div className="absolute left-2.5 top-1.5 w-5 h-5 rounded-full bg-accent-500 border-2 border-background-50"></div>
                      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                        <span className="text-xs font-semibold text-accent-600">{m.year}</span>
                        <h4 className="text-sm font-semibold text-foreground-950 mt-1 mb-1">{m.title}</h4>
                        <p className="text-sm text-foreground-500">{m.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <span className="text-sm font-semibold text-primary-600 uppercase tracking-wider">联系我们</span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                  期待与你的交流
                </h2>
                <p className="text-foreground-500 mt-3 max-w-xl mx-auto">
                  无论你是想合作、投稿还是咨询问题，都欢迎联系我们。
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                <div className="lg:col-span-2 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 shrink-0">
                      <i className="ri-wechat-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 shrink-0">
                      <i className="ri-phone-line text-secondary-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">联系电话</h4>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 shrink-0">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">办公地址</h4>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <form
                    id="contact-form"
                    data-readdy-form=""
                    onSubmit={handleSubmit}
                    className="bg-background-50 rounded-lg border border-background-200/70 p-6 space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 *
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 *
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题 *
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 *
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />

                    <button
                      type="submit"
                      className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      发送留言
                    </button>

                    {formStatus === 'success' && (
                      <div className="p-3 rounded-md bg-accent-100 text-accent-800 text-sm">
                        <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className="ri-check-line"></i>
                        </span>
                        {formMessage}
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="p-3 rounded-md bg-primary-100 text-primary-800 text-sm">
                        <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className="ri-error-warning-line"></i>
                        </span>
                        {formMessage}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}